/**
 * The amenity picker — a search box over a grouped icon grid.
 *
 * WHY IT CHANGED (2026-08-21). The library went from 4 hardcoded icons to 43 read
 * live out of Layne's SFMC folder, and the old one-icon-per-row list turned that
 * into 1,848px of scrolling inside a 346px-wide panel — measured, not estimated.
 * Jack picked option D from the mockup set ("d with a search bar up top"): a
 * 4-across grid, sectioned by the SFMC subfolder each icon lives in, with the
 * section header sticking while you scroll it. Measured 1,383px, 25% shorter, and
 * more to the point someone hunting for a pool now knows to look under Water.
 *
 * THE GROUPS ARE NOT DEFINED HERE. Each icon carries the name of the SFMC subfolder
 * it sits in, and the worker sends the folder order alongside. That is deliberate:
 * Layne owns the taxonomy in Content Builder, so a new subfolder or a moved icon
 * shows up here with no code change, and this file never becomes a second opinion
 * about where "Splash Park" belongs. Icons with no group fall into "Other" — which
 * is also what the whole library looks like on the fallback path, where the picker
 * degrades to one plain grid rather than one bogus section.
 *
 * SEARCH MATCHES THE GROUP NAME TOO, so typing "water" finds the Water section
 * rather than only the two labels containing the word.
 */
import { useMemo, useRef, useState } from "react";
import type { AmenIcon } from "../services/echAmen";

/** "Other" is the bucket for an icon whose group is unknown — always rendered last */
export const AMEN_OTHER = "Other";

export interface AmenGroup {
  name: string;
  icons: AmenIcon[];
}

/**
 * Bucket the library by group and order the buckets by `order`.
 *
 * Exported for its own test: the ordering is the part that silently rots when the
 * folder changes, and a wrong order is invisible in a screenshot.
 */
export function amenGroups(lib: AmenIcon[], order: string[]): AmenGroup[] {
  const by: Record<string, AmenIcon[]> = {};
  const seen: string[] = [];
  (lib || []).forEach((i) => {
    const g = String(i.group || "").trim() || AMEN_OTHER;
    if (!by[g]) { by[g] = []; seen.push(g); }
    by[g].push(i);
  });
  /* the server's order first, then anything it did not mention (in first-seen
     order), then Other last however it arrived — an icon is never dropped for
     being in an unexpected group */
  const out: string[] = [];
  (order || []).forEach((g) => { if (by[g] && g !== AMEN_OTHER && out.indexOf(g) === -1) out.push(g); });
  seen.forEach((g) => { if (g !== AMEN_OTHER && out.indexOf(g) === -1) out.push(g); });
  if (by[AMEN_OTHER]) out.push(AMEN_OTHER);
  return out.map((name) => ({ name, icons: by[name] }));
}

/** case- and space-insensitive contains, used for both the label and the group */
function hit(hay: string, needle: string): boolean {
  return hay.toLowerCase().indexOf(needle) > -1;
}

/**
 * Filter groups by a query. A group whose NAME matches keeps all its icons, so
 * "water" gives you the section rather than a two-icon fragment of it.
 */
export function amenFilter(groups: AmenGroup[], q: string): AmenGroup[] {
  const needle = String(q || "").trim().toLowerCase();
  if (!needle) return groups;
  const out: AmenGroup[] = [];
  groups.forEach((g) => {
    if (hit(g.name, needle)) { out.push(g); return; }
    const icons = g.icons.filter((i) => hit(i.label, needle));
    if (icons.length) out.push({ name: g.name, icons });
  });
  return out;
}

export default function AmenPicker({
  lib,
  order,
  picked,
  onPick,
}: {
  lib: AmenIcon[];
  order: string[];
  /** the labels currently in the block, lower-cased comparison happens here */
  picked: string[];
  onPick: (label: string) => void;
}) {
  const [q, setQ] = useState("");
  const box = useRef<HTMLDivElement | null>(null);

  const groups = useMemo(() => amenGroups(lib, order), [lib, order]);
  const shown = useMemo(() => amenFilter(groups, q), [groups, q]);

  const on: Record<string, 1> = {};
  (picked || []).forEach((p) => { on[String(p).toLowerCase()] = 1; });

  /* one unnamed group means there is no taxonomy to show — the fallback library,
     or a folder read that came back flat. Headers there would be noise. */
  const bare = groups.length === 1 && groups[0].name === AMEN_OTHER;
  const total = lib.length;
  const found = shown.reduce((n, g) => n + g.icons.length, 0);

  return (
    <div className="ampick">
      <div className="amq">
        <input
          className="amqi"
          type="search"
          value={q}
          placeholder={"Search " + total + " amenity icons…"}
          aria-label="Search amenity icons"
          onChange={(e) => {
            setQ(e.target.value);
            /* a filtered list is short — leaving the scroll where it was makes it
               look empty */
            if (box.current) box.current.scrollTop = 0;
          }}
        />
        {q.trim() !== "" && (
          <button type="button" className="amqx" aria-label="Clear search" onClick={() => setQ("")}>
            ×
          </button>
        )}
      </div>

      <div className="amscroll" ref={box}>
        {found === 0 && (
          <div className="amnone">
            No icon matches “{q.trim()}”.
            <br />
            <span className="amnone-sub">Names come from the SFMC Amenity Icons folder.</span>
          </div>
        )}

        {shown.map((g) => (
          <div className="amgrp" key={g.name}>
            {!bare && (
              <div className="amgh">
                {g.name} <span className="amgc">{g.icons.length}</span>
              </div>
            )}
            <div className="amgrid">
              {g.icons.map((a) => {
                const sel = !!on[a.label.toLowerCase()];
                return (
                  <button
                    key={a.label}
                    type="button"
                    className={"amtile" + (sel ? " on" : "")}
                    data-amtog={a.label}
                    aria-pressed={sel}
                    title={a.label}
                    onClick={() => onPick(a.label)}
                  >
                    <span className="amtk" aria-hidden="true">✓</span>
                    <img src={a.url} alt="" />
                    <span className="amtl">{a.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {q.trim() !== "" && found > 0 && (
        <div className="amfound">
          {found} of {total} shown
        </div>
      )}
    </div>
  );
}
